(function () {
    const BASE = "/assets/front/images/finishes/";

    let _finishes = {};
    let _aliases = {};

    const norm = (v) =>
        String(v || "")
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "-");
    const esc = (s) =>
        String(s ?? "")
            .replace(/&/g, "&amp;")
            .replace(/"/g, "&quot;")
            .replace(/</g, "&lt;");

    function generateAliases() {
        _aliases = {};
        Object.entries(_finishes).forEach(([key, item]) => {
            const label = item.label || key;
            const words = label.toLowerCase().split(/\s+/);

            // alias: first two letters of key
            const shortKey = key.slice(0, 2);

            // alias: first letters of words in label
            const initials = words.map((w) => w[0]).join("");

            // register
            _aliases[shortKey] = key;
            _aliases[initials] = key;
        });
    }

    const FinishRegistry = {
        get(key) {
            const k = norm(key);
            const finalKey = _finishes[k] ? k : _aliases[k] || k;
            return _finishes[finalKey] || null;
        },
        has(key) {
            return !!this.get(key);
        },
        list() {
            return Object.entries(_finishes).map(([k, v]) => ({ key: k, ...v }));
        },
        extend(map) {
            Object.entries(map || {}).forEach(([k, v]) => {
                const key = norm(k);
                if (v && v.src)
                    _finishes[key] = {
                        label: v.label || key,
                        src: v.src,
                        src2x: v.src2x || undefined,
                    };
            });
            generateAliases();
        },
        setBasePath(path) {
            Object.keys(_finishes).forEach((k) => {
                const file = _finishes[k].src.split("/").pop();
                const file2 = _finishes[k].src2x
                    ? _finishes[k].src2x.split("/").pop()
                    : null;
                _finishes[k].src = `${path}${file}`;
                if (file2) _finishes[k].src2x = `${path}${file2}`;
            });
        },

        async loadFromApi(url) {
            try {
                const data = await ajaxDynamicMethod("/get-finishes", "GET");
                _finishes = {};
                this.extend(data.data.finishes);
                document.querySelectorAll("ui-finish").forEach((el) => el.render());
            } catch (err) {
                console.error("Failed to load finishes:", err);
            }
        },
        _aliases() {
            return { ..._aliases };
        },
    };
    window.FinishRegistry = FinishRegistry;

    $(document).ready(function () {
        FinishRegistry.loadFromApi("/api/finishes");
    });

    // ---------- <ui-finish> component ----------
    const SIZE_TOKENS = { xs: 14, sm: 18, md: 24, lg: 32, xl: 40 };

    function humanize(key) {
        return String(key || "")
            .replace(/[-_]+/g, " ")
            .replace(/\b\w/g, (m) => m.toUpperCase())
            .trim();
    }

    function tmpl(data) {
        const {
            classes,
            sizePx,
            img,
            displayLabel,
            tipMode,
            tipText,
            lazy,
            ring,
            ariaDisabled,
            ariaReadonly,
        } = data;
        const srcset = img.src2x ? ` srcset="${img.src2x} 2x"` : "";
        const loading = lazy ? ' loading="lazy"' : "";
        const aria = `role="img" aria-label="${esc(displayLabel)}"${ariaDisabled ? ' aria-disabled="true"' : ""
            }${ariaReadonly ? ' aria-readonly="true"' : ""}`;
        const tipAttr =
            tipMode === "title"
                ? ` title="${esc(tipText)}"`
                : tipMode === "tt"
                    ? ` tt="${esc(tipText)}"`
                    : "";

        if (classes.includes("finish--chip")) {
            return `
        <span class="${classes}" style="--finish-size:${sizePx}px;" ${aria}${tipAttr}>
          <span class="finish__dot ${ring ? "is-ring" : ""}">
            <img class="finish__img" src="${img.src
                }"${srcset}${loading} alt="${esc(displayLabel)}">
          </span>
          <span class="finish__label">${esc(displayLabel)}</span>
        </span>
      `;
        }
        return `
      <span class="${classes}" style="--finish-size:${sizePx}px;" ${aria}${tipAttr}>
        <img class="finish__img" src="${img.src}"${srcset}${loading} alt="${esc(
            displayLabel
        )}">
      </span>
    `;
    }

    class UIFinish extends HTMLElement {
        static get observedAttributes() {
            return [
                "name",
                "size",
                "variant",
                "shape",
                "label",
                "ring",
                "lazy",
                "tooltip",
                "tt",
                "callout",
                "disabled",
                "readonly",
            ];
        }
        connectedCallback() {
            this.render();
        }
        attributeChangedCallback() {
            this.render();
        }

        _attr(name, fallback = null) {
            return this.getAttribute(name) ?? this.dataset[name] ?? fallback;
        }

        render() {
            const rawName = this._attr("name", this._attr("data-name"));
            if (!rawName) {
                this.innerHTML = "";
                return;
            }

            const variant = (this._attr("variant", "dot") || "dot").toLowerCase();
            const shape = (this._attr("shape", "circle") || "circle").toLowerCase();
            const ring = this.hasAttribute("ring") || this.dataset.ring !== undefined;
            const lazy = this.hasAttribute("lazy") || this.dataset.lazy !== undefined;
            const hasCallout =
                this.hasAttribute("callout") || this.dataset.callout !== undefined;

            const isDisabled =
                this.hasAttribute("disabled") || this.dataset.disabled !== undefined;
            const isReadonly =
                this.hasAttribute("readonly") || this.dataset.readonly !== undefined;

            let sizeAttr = this._attr("size", "md");
            let sizePx = SIZE_TOKENS[sizeAttr] || parseInt(sizeAttr, 10);
            if (!sizePx || Number.isNaN(sizePx)) sizePx = SIZE_TOKENS.md;

            const item = window.FinishRegistry.get(rawName);
            const baseLabel = item && item.label ? item.label : humanize(rawName);
            const displayLabel = this._attr("label") || baseLabel; // visual chip text can be overridden

            // Determine tooltip text (registry-only by default)
            let tipMode = null,
                tipText = null;
            const hasTT = this.hasAttribute("tt") || this.dataset.tt !== undefined;
            const ttValue = this.getAttribute("tt") ?? this.dataset.tt;
            if (hasTT) {
                tipMode = "tt";
                tipText = ttValue && ttValue.trim() ? ttValue : baseLabel;
            } else if (
                (this.hasAttribute("tooltip") || this.dataset.tooltip !== undefined) &&
                !hasCallout
            ) {
                const raw = this.getAttribute("tooltip") ?? this.dataset.tooltip ?? "";
                tipMode = "title";
                tipText = String(raw).trim() !== "" ? raw : baseLabel;
            }

            // image fallback
            const image = item || {
                label: baseLabel,
                src: `data:image/svg+xml;utf8,${encodeURIComponent(
                    this._fallbackSVG(baseLabel)
                )}`,
            };

            const classes = [
                "finish",
                `finish--${variant}`,
                `finish--${shape}`,
                isDisabled ? "is-disabled" : "",
                isReadonly ? "is-readonly" : "",
                !item ? "finish--fallback" : "",
            ]
                .filter(Boolean)
                .join(" ");

            // reflect ARIA on host as well
            if (isDisabled) this.setAttribute("aria-disabled", "true");
            else this.removeAttribute("aria-disabled");
            if (isReadonly) this.setAttribute("aria-readonly", "true");
            else this.removeAttribute("aria-readonly");

            this.innerHTML = tmpl({
                classes,
                sizePx,
                img: image,
                displayLabel,
                tipMode,
                tipText,
                lazy,
                ring,
                ariaDisabled: isDisabled,
                ariaReadonly: isReadonly,
            });
        }

        _fallbackSVG(text) {
            const t = String(text || "?")
                .trim()
                .slice(0, 2)
                .toUpperCase();
            return `
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">
          <defs>
            <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%"  stop-color="#826a43"/>
              <stop offset="100%" stop-color="#d4af37"/>
            </linearGradient>
          </defs>
          <circle cx="32" cy="32" r="30" fill="url(#g)"/>
          <text x="50%" y="52%" text-anchor="middle" font-size="22" fill="#fff"
                font-family="system-ui,Segoe UI,Roboto" dy=".35em">${t}</text>
        </svg>
      `;
        }
    }

    if (!customElements.get("ui-finish")) {
        customElements.define("ui-finish", UIFinish);
    }

    // helper to render a batch (unchanged)
    window.renderFinishList = function (container, names = [], opts = {}) {
        const el = (n) => {
            const f = document.createElement("ui-finish");
            f.setAttribute("name", n);
            if (opts.size) f.setAttribute("size", opts.size);
            if (opts.variant) f.setAttribute("variant", opts.variant);
            if (opts.shape) f.setAttribute("shape", opts.shape);
            if (opts.ring) f.setAttribute("ring", "");
            if (opts.lazy) f.setAttribute("lazy", "");
            if (opts.tooltip)
                f.setAttribute(
                    "tooltip",
                    typeof opts.tooltip === "string" ? opts.tooltip : ""
                );
            if (opts.callout)
                f.setAttribute(
                    "callout",
                    typeof opts.callout === "string" ? opts.callout : ""
                );
            if (opts.disabled) f.setAttribute("disabled", "");
            if (opts.readonly) f.setAttribute("readonly", "");
            return f;
        };
        names.forEach((n) => container.appendChild(el(n)));
    };

    // ---------- Utilities for toggles & chips ----------
    function resolveMatch(codeOrName) {
        const k = norm(codeOrName);
        const canon = FinishRegistry._aliases[k] || k;
        return canon; // canonical key if alias exists, else original (already canonical or unknown)
    }

    function matchesToggle(labelEl, codesSet) {
        const input = labelEl.querySelector(".finishToggle__input");
        const ui = labelEl.querySelector("ui-finish");
        if (!input || !ui) return false;
        const alias = norm(input.value || "");
        const name = norm(ui.getAttribute("name") || "");
        return (
            codesSet.has(alias) ||
            codesSet.has(name) ||
            codesSet.has(resolveMatch(alias)) ||
            codesSet.has(resolveMatch(name))
        );
    }

    // Disable specified toggles (checkbox or radio). Optionally append callout text.
    function disableToggles(
        container,
        codes = [],
        { addClass = "is-disabled", calloutText = null } = {}
    ) {
        const set = new Set(codes.map(resolveMatch));
        container.querySelectorAll(".finishToggle").forEach((lbl) => {
            if (!matchesToggle(lbl, set)) return;
            const input = lbl.querySelector(".finishToggle__input");
            const ui = lbl.querySelector("ui-finish");
            if (input) input.disabled = true;
            if (ui) ui.setAttribute("disabled", "");
            if (addClass) lbl.classList.add(addClass);
            lbl.setAttribute("aria-disabled", "true");
            if (ui && calloutText) {
                const base =
                    (FinishRegistry.get(ui.getAttribute("name")) || {}).label ||
                    ui.getAttribute("name") ||
                    "";
                ui.setAttribute("callout", "");
                ui.setAttribute("tt", `${base} — ${calloutText}`);
            }
        });
    }

    // Make specified toggles read-only (locked selection). Adds hidden mirrors so values submit.
    function readonlyToggles(
        container,
        codes = [],
        { addClass = "is-readonly", mirrorUnchecked = false } = {}
    ) {
        const set = new Set(codes.map(resolveMatch));
        container.querySelectorAll(".finishToggle").forEach((lbl) => {
            if (!matchesToggle(lbl, set)) return;
            const input = lbl.querySelector(".finishToggle__input");
            const ui = lbl.querySelector("ui-finish");
            if (!input || !ui) return;
            // lock the visible input
            input.disabled = true;
            ui.setAttribute("readonly", "");
            lbl.classList.add(addClass);
            lbl.setAttribute("aria-disabled", "true");

            // ensure submission:
            // radios -> always mirror currently checked one
            // checkboxes -> mirror only if checked (unless mirrorUnchecked=true)
            const mustMirror =
                input.type === "radio"
                    ? input.checked
                    : mirrorUnchecked
                        ? true
                        : input.checked;
            if (mustMirror) {
                let mirror = lbl.querySelector(
                    `input[type="hidden"][name="${input.name}"]`
                );
                if (!mirror) {
                    mirror = document.createElement("input");
                    mirror.type = "hidden";
                    mirror.name = input.name;
                    lbl.appendChild(mirror);
                }
                mirror.value = input.value;
            }
        });
    }

    // Expose helpers
    window.FinishControls = {
        disableToggles,
        readonlyToggles,
        // simple chip helpers (non-toggle)
        setChipDisabled(el, flag = true) {
            if (!el) return;
            if (flag) el.setAttribute("disabled", "");
            else el.removeAttribute("disabled");
        },
        setChipReadonly(el, flag = true) {
            if (!el) return;
            if (flag) el.setAttribute("readonly", "");
            else el.removeAttribute("readonly");
        },
    };
})();
